import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useAuth } from "@/hooks/useAuth";
import { useWallet, clearWalletCache } from "@/hooks/useWallet";
import { canAccessVIP } from "@/lib/membership";
import AuthModal from "@/pages/subscribe-trial/components/AuthModal";
import OrderSuccessModal from "@/pages/subscribe-trial/components/OrderSuccessModal";

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  sellingPrice: number;
  returnRate: number;
  raised: number;
  target: number;
  stockQuantity: number;
  type: "trial" | "vip";
  status: string;
  imageUrl?: string;
}

// Helper: preload image into browser cache for instant display
function preloadImage(url: string): Promise<void> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => resolve();
    img.src = url;
  });
}

export default function ProductDetailPage() {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const { isLoggedIn, profile, user } = useAuth();
  const isVIP = canAccessVIP(profile?.membership_level);

  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showInsufficientBalance, setShowInsufficientBalance] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [quantityInput, setQuantityInput] = useState("1");
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showInsufficientStock, setShowInsufficientStock] = useState(false);

  const { wallet } = useWallet();

  const fetchProduct = async (signal?: AbortSignal) => {
    if (!productId) return;

    try {
      const { data, error: fetchError } = await supabase
        .from("products")
        .select("id,name,description,price,selling_price,return_rate,raised,target,stock_quantity,type,status,image_url")
        .eq("id", productId)
        .single()
        .abortSignal(signal || new AbortController().signal);

      if (signal?.aborted) return;

      if (fetchError) {
        setError("貨源不存在或已下架");
        setProduct(null);
        setLoading(false);
        return;
      }

      const sp = data.selling_price ?? Math.round(data.price * (1 + Number(data.return_rate || 25) / 100));
      const productData: Product = {
        id: data.id,
        name: data.name,
        description: data.description || "",
        price: data.price,
        sellingPrice: sp,
        returnRate: Number(data.return_rate) || 25,
        raised: data.raised,
        target: data.target,
        stockQuantity: data.stock_quantity || 0,
        type: data.type as "trial" | "vip",
        status: data.status,
        imageUrl: data.image_url || undefined,
      };

      setProduct(productData);
      try {
        const cacheKey = `product_detail_${productId}`;
        sessionStorage.setItem(cacheKey, JSON.stringify({ product: productData, _cachedAt: Date.now() }));
      } catch {
        // ignore storage errors
      }

      if (productData.imageUrl) {
        preloadImage(productData.imageUrl);
      }
    } catch (err: unknown) {
      if (signal?.aborted) return;
      console.error("[ProductDetail] fetchProduct error:", err);
      setError("貨源不存在或已下架");
      setProduct(null);
    }

    if (!signal?.aborted) {
      setLoading(false);
    }
  };

  // Unified data loading: cache-first + background refresh with non-blocking fetch
  useEffect(() => {
    setImgLoaded(false);
    setImgError(false);
    setError("");

    if (!productId) {
      setLoading(false);
      setProduct(null);
      return;
    }

    const controller = new AbortController();
    let cancelled = false;
    const cacheKey = `product_detail_${productId}`;
    let cacheHit = false;

    // Step 1: Read cache synchronously (non-blocking)
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        const ttl = 5 * 60 * 1000;
        if (Date.now() - parsed._cachedAt < ttl) {
          cacheHit = true;
          setProduct(parsed.product);
          setLoading(false);
          if (parsed.product?.imageUrl) {
            preloadImage(parsed.product.imageUrl);
          }
        }
      }
    } catch {
      // ignore cache read errors
    }

    if (!cacheHit) {
      setLoading(true);
      setProduct(null);
    }

    // Step 2: Fetch from API — deferred to next tick to unblock first paint
    const timer = setTimeout(() => {
      if (cancelled || controller.signal.aborted) return;
      fetchProduct(controller.signal);
    }, 0);

    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(timer);
    };
  }, [productId]);

  useEffect(() => {
    setQuantityInput(String(quantity));
  }, [quantity]);

  const remaining = product
    ? Math.max(
        0,
        product.stockQuantity - product.raised,
      )
    : 0;
  const profit = product ? product.sellingPrice - product.price : 0;

  const saleProgress = product
    ? product.stockQuantity > 0
        ? Math.min(
            100,
            Math.round(
              (product.raised / product.stockQuantity) * 100,
            ),
          )
        : 0
    : 0;

  const handleQuantityChange = (val: number) => {
    const maxQty = Math.max(1, remaining);
    if (val < 1) {
      setQuantity(1);
      return;
    }
    if (val > maxQty) {
      setQuantity(maxQty);
      return;
    }
    setQuantity(val);
  };

  const handleQuantityInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (!/^\d*$/.test(val)) return;
    setQuantityInput(val);
  };

  const handleQuantityInputBlur = () => {
    let val = parseInt(quantityInput, 10);
    if (Number.isNaN(val) || val < 1) val = 1;
    const maxQty = Math.max(1, remaining);
    if (val > maxQty) val = maxQty;
    setQuantity(val);
    setQuantityInput(String(val));
  };

  const handleConfirm = () => {
    if (!isLoggedIn) {
      setShowAuthModal(true);
      return;
    }
    if (product?.type === "vip" && !isVIP) {
      return;
    }
    if (!agreed) {
      return;
    }
    // 庫存前置檢查：最優先確認剩餘可認購數量足夠
    if (remaining < quantity) {
      setShowInsufficientStock(true);
      return;
    }
    // 餘額檢查：讀取 wallets 表 balance
    const availableBalance = wallet?.balance ?? 0;
    if (availableBalance < totalPrice) {
      setShowInsufficientBalance(true);
      return;
    }
    submitOrder();
  };

  const submitOrder = async () => {
    if (!product || !user) {
      console.error("[ProductDetail] submitOrder aborted: missing product or user");
      return;
    }
    setIsSubmitting(true);
    setError("");

    try {
      const { data, error: rpcError } = await supabase.rpc(
        "create_order_and_deduct",
        {
          p_user_id: user.id,
          p_product_id: product.id,
          p_amount: totalPrice,
          p_status: "active",
          p_product_name: product.name,
          p_return_rate: product.returnRate,
          p_selling_amount: product.sellingPrice * quantity,
          p_quantity: quantity,
        },
      );

      if (rpcError) {
        console.error("[ProductDetail] RPC failed — code:", rpcError.code);
        console.error("[ProductDetail] RPC failed — message:", rpcError.message);
        console.error("[ProductDetail] RPC failed — details:", rpcError.details);
        setError("訂單建立失敗，請稍後重試");
        setIsSubmitting(false);
        return;
      }

      if (!data || !data.success) {
        const msg = data?.error || "訂單建立失敗";
        console.error("[ProductDetail] RPC business error:", msg);
        if (msg === "餘額不足，請先充值！") {
          setShowInsufficientBalance(true);
        } else {
          setError(msg);
        }
        setIsSubmitting(false);
        return;
      }

      console.log("[ProductDetail] Order created & deducted:", data);

      // 即時刷新前端 UI：手動更新本地 raised
      setProduct((prev) =>
        prev ? { ...prev, raised: prev.raised + quantity } : null
      );

      // 獨立更新 products.raised，即使失敗也絕不影響已成立的訂單
      try {
        const { error: raisedUpdateError } = await supabase
          .from("products")
          .update({ raised: product.raised + quantity })
          .eq("id", product.id);
        if (raisedUpdateError) {
          console.error("[ProductDetail] 更新 raised 失敗:", raisedUpdateError);
        }
      } catch (err: unknown) {
        console.error("[ProductDetail] 更新 raised 異常:", err);
      }

      try {
        const cacheKey = `product_detail_${productId}`;
        sessionStorage.removeItem(cacheKey);
      } catch {
        // ignore
      }
      // 背景重新 fetch 確保資料一致性
      fetchProduct();

      // 清除本地快取，確保下次進入頁面讀取最新扣款後餘額
      if (user?.id) clearWalletCache(user.id);
      // 同步刷新訂單列表與錢包餘額
      window.dispatchEvent(new CustomEvent("orders:refresh"));
      window.dispatchEvent(new CustomEvent("wallet:refresh"));
      setShowSuccess(true);
    } catch (err: unknown) {
      console.error("[ProductDetail] Order submit exception:", err);
      setError("訂單建立時發生異常，請稍後重試");
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalPrice = product ? product.price * quantity : 0;
  const totalProfit = profit * quantity;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main className="pt-20 md:pt-24">
        <div className="w-full px-4 md:px-8 lg:px-12 py-8 md:py-12">
          <div className="max-w-5xl mx-auto">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 mb-6">
              <button
                onClick={() => navigate("/shop")}
                className="font-body text-xs text-stone-500 hover:text-stone-800 transition-colors"
              >
                全部貨源
              </button>
              <i className="ri-arrow-right-s-line text-stone-300 text-xs" />
              <span className="font-body text-xs text-stone-800">
                {product?.name || (loading ? "載入中..." : "貨源詳情")}
              </span>
            </div>

            {!loading && (error || !product) ? (
              <div className="text-center py-20">
                <i className="ri-error-warning-line text-4xl text-stone-300 mb-3" />
                <p className="font-body text-lg text-stone-600">{error || "貨源不存在"}</p>
                <button
                  onClick={() => navigate("/shop")}
                  className="mt-4 font-body text-sm text-rose-700 hover:underline"
                >
                  返回全部貨源
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
                {/* Left: Product Image */}
                <div className="flex items-center justify-center">
                  <div className="relative w-full max-w-md aspect-square rounded-2xl bg-stone-100 overflow-hidden flex items-center justify-center border border-stone-200">
                    {product?.imageUrl && !imgError && (
                      <img
                        src={product.imageUrl}
                        alt={product.name}
                        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${imgLoaded ? "opacity-100" : "opacity-0"}`}
                        fetchPriority="high"
                        loading="eager"
                        onLoad={() => setImgLoaded(true)}
                        onError={() => setImgError(true)}
                      />
                    )}

                    {/* Fallback / Loading placeholder overlay */}
                    <div className={`absolute inset-0 flex flex-col items-center justify-center transition-opacity duration-300 ${product?.imageUrl && !imgError && imgLoaded ? "opacity-0 pointer-events-none" : "opacity-100"}`}>
                      {loading ? (
                        <div className="flex flex-col items-center gap-3">
                          <div className="w-20 h-20 rounded-2xl bg-stone-200 animate-pulse" />
                          <div className="h-4 w-24 bg-stone-200 rounded animate-pulse" />
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-3">
                          <div
                            className={`w-20 h-20 rounded-2xl flex items-center justify-center ${product?.type === "trial" ? "bg-rose-100" : "bg-amber-100"}`}
                          >
                            <i
                              className={`text-3xl ${product?.type === "trial" ? "ri-sparkling-line text-rose-600" : "ri-vip-crown-line text-amber-600"}`}
                            />
                          </div>
                          <span className="font-body text-sm text-stone-400">
                            {product?.name}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Type badge */}
                    {!loading && product && (
                      <div className="absolute top-4 left-4">
                        {product.type === "trial" ? (
                          <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-rose-800 text-white font-body text-xs font-semibold">
                            <i className="ri-sparkling-line" />
                            體驗區
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-amber-500 text-stone-900 font-body text-xs font-semibold">
                            <i className="ri-vip-crown-line" />
                            VIP 區
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Right: Info & Subscription */}
                <div className="flex flex-col gap-5">
                  {loading || !product ? (
                    <>
                      {/* Skeleton Title */}
                      <div className="h-8 w-3/4 bg-stone-200 rounded-lg animate-pulse" />
                      {/* Skeleton Description */}
                      <div className="space-y-2">
                        <div className="h-4 w-full bg-stone-200 rounded animate-pulse" />
                        <div className="h-4 w-5/6 bg-stone-200 rounded animate-pulse" />
                      </div>
                      {/* Skeleton Stats */}
                      <div className="grid grid-cols-2 gap-3">
                        {[1, 2, 3, 4].map((i) => (
                          <div key={i} className="h-20 bg-stone-50 rounded-xl border border-stone-100 animate-pulse" />
                        ))}
                      </div>
                      {/* Skeleton Progress */}
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <div className="h-3 w-24 bg-stone-200 rounded animate-pulse" />
                          <div className="h-3 w-20 bg-stone-200 rounded animate-pulse" />
                        </div>
                        <div className="h-2.5 bg-stone-100 rounded-full overflow-hidden">
                          <div className="h-full w-1/3 bg-stone-200 rounded-full animate-pulse" />
                        </div>
                      </div>

                      {/* Skeleton Quantity removed */}

                      {/* Skeleton Totals */}
                      <div className="flex items-center justify-between py-3 border-t border-stone-200">
                        <div className="h-4 w-16 bg-stone-200 rounded animate-pulse" />
                        <div className="h-6 w-24 bg-stone-200 rounded animate-pulse" />
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="h-4 w-16 bg-stone-200 rounded animate-pulse" />
                        <div className="h-5 w-20 bg-stone-200 rounded animate-pulse" />
                      </div>
                      {/* Skeleton Agreement */}
                      <div className="h-20 bg-stone-50 rounded-xl border border-stone-200 animate-pulse" />
                      {/* Skeleton CTA */}
                      <div className="h-12 w-full bg-stone-200 rounded-full animate-pulse" />
                    </>
                  ) : (
                    <>
                      <h1 className="font-display text-2xl md:text-3xl font-bold text-stone-800">
                        {product.name}
                      </h1>
                      <p className="font-body text-sm text-stone-500 leading-relaxed">
                        {product.description}
                      </p>

                      {/* Stats grid */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-stone-50 rounded-xl p-4 border border-stone-100">
                          <p className="font-body text-[10px] text-stone-400">認購價</p>
                          <p className="font-display text-lg font-bold text-stone-800">
                            NT$ {product.price.toLocaleString()}
                          </p>
                        </div>
                        <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
                          <p className="font-body text-[10px] text-emerald-600">銷售價</p>
                          <p className="font-display text-lg font-bold text-emerald-700">
                            NT$ {product.sellingPrice.toLocaleString()}
                          </p>
                        </div>
                        <div className="bg-stone-50 rounded-xl p-4 border border-stone-100">
                          <p className="font-body text-[10px] text-stone-400">利潤</p>
                          <p className="font-display text-lg font-bold text-emerald-600">
                            NT$ {profit.toLocaleString()}
                          </p>
                        </div>
                        <div className="bg-stone-50 rounded-xl p-4 border border-stone-100">
                          <p className="font-body text-[10px] text-stone-400">
                            {product.type === "trial" ? "限購組數" : "必購組數"}
                          </p>
                          <p className="font-display text-lg font-bold text-stone-800">
                            {remaining.toLocaleString()} 組
                          </p>
                        </div>
                      </div>

                      {/* Progress bar */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-body text-xs text-stone-500">
                            已認購 {product.raised} / {product.stockQuantity} 組
                          </span>
                          <span className="font-body text-xs text-stone-500">
                            剩餘 {remaining} 組
                          </span>
                        </div>
                        <div className="w-full h-2.5 rounded-full bg-stone-100 overflow-hidden">
                          <div
                            className="h-full rounded-full bg-amber-500 transition-all duration-700"
                            style={{ width: `${saleProgress}%` }}
                          />
                        </div>
                      </div>

                      {/* Quantity selector removed */}

                      {/* VIP check */}
                      {product.type === "vip" && !isVIP && (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                          <div className="flex items-center gap-2">
                            <i className="ri-lock-line text-amber-600" />
                            <p className="font-body text-sm font-semibold text-amber-700">
                              VIP 專屬貨源
                            </p>
                          </div>
                          <p className="font-body text-xs text-amber-600 mt-1">
                            升級為VIP會員或以上等級後即可認購此貨源
                          </p>
                          <Link
                            to="/vip-subscribe"
                            className="inline-block mt-2 font-body text-xs font-semibold px-4 py-2 rounded-full bg-amber-500 text-stone-900 hover:bg-amber-400 transition-colors whitespace-nowrap"
                          >
                            升級會員
                          </Link>
                        </div>
                      )}

                      {/* Agreement */}
                      {/* 認購協議勾選框已隱藏 */}

                      {/* CTA */}
                      <button
                        disabled
                        className="w-full font-body text-sm font-semibold py-3.5 rounded-full whitespace-nowrap bg-stone-200 text-stone-400 cursor-not-allowed"
                      >
                        認購請洽供應商
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />

      {/* Insufficient Stock Modal */}
      {showInsufficientStock && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl p-6 max-w-sm mx-4 text-center shadow-lg">
            <div className="w-12 h-12 rounded-full bg-rose-50 flex items-center justify-center mx-auto mb-3">
              <i className="ri-error-warning-line text-rose-600 text-xl" />
            </div>
            <h3 className="font-display text-lg font-bold text-stone-800 mb-2">
              庫存不足
            </h3>
            <p className="font-body text-sm text-stone-500 mb-4">
              {product?.type === "trial"
                ? `抱歉，該商品庫存組數不足！目前剩餘 ${remaining} 組，您欲購買 ${quantity} 組。`
                : `抱歉，該商品剩餘可認購數量不足！目前剩餘 ${remaining} 件，您欲購買 ${quantity} 件。`}
            </p>
            <button
              onClick={() => setShowInsufficientStock(false)}
              className="w-full font-body text-sm font-semibold py-2.5 rounded-full bg-rose-800 text-white hover:bg-rose-900 transition-colors whitespace-nowrap"
            >
              知道了
            </button>
          </div>
        </div>
      )}

      {/* Insufficient Balance Modal */}
      {showInsufficientBalance && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl p-6 max-w-sm mx-4 text-center shadow-lg">
            <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-3">
              <i className="ri-error-warning-line text-amber-600 text-xl" />
            </div>
            <h3 className="font-display text-lg font-bold text-stone-800 mb-2">
              餘額不足
            </h3>
            <p className="font-body text-sm text-stone-500 mb-4">
              您的可用餘額為 NT$ {(wallet?.balance ?? 0).toLocaleString()}，不足以支付本次認購金額 NT$ {totalPrice.toLocaleString()}。
            </p>
            <p className="font-body text-xs text-stone-400 mb-5">
              請先充值後再試。
            </p>
            <button
              onClick={() => setShowInsufficientBalance(false)}
              className="w-full font-body text-sm font-semibold py-2.5 rounded-full bg-rose-800 text-white hover:bg-rose-900 transition-colors whitespace-nowrap"
            >
              知道了
            </button>
          </div>
        </div>
      )}

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={() => {
          if (agreed) submitOrder();
        }}
      />

      <OrderSuccessModal
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        productName={product?.name || ""}
      />
    </div>
  );
}